import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { WA_WINDOW } from '@ng-web-apis/common';
import { Store } from '@ngrx/store';
import { VideoUploadHttpService } from '@services/http/video-upload.http.service';
import { lifeCycleActions } from '@shared/store/life-cycle/life-cycle.actions';

/**
 * Identity lives in the auth app (login, register, logout, profile): the webapp only leaves for it,
 * passing a `redirect_uri` the auth app sends the user back to once done.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthAppRedirectService {
  private readonly window = inject(WA_WINDOW);
  private readonly store = inject(Store);
  private readonly videoUploadService = inject(VideoUploadHttpService);

  /** User-initiated: the auth app revokes the session, then shows its login page. */
  logout(): void {
    this.leaveSession();
    this.window.location.assign(getAuthAppUrl('/logout', { redirect_uri: `${environment.appUrl}/home` }));
  }

  /** No usable session (missing, expired, not validated yet): sign in, then come back to `appPath` or the current page. */
  login(appPath?: string): void {
    this.leaveSession();
    const returnUrl = appPath === undefined ? this.window.location.href : toAppUrl(appPath);
    this.window.location.assign(getAuthAppUrl('/login', { redirect_uri: returnUrl }));
  }

  /** Profile & security in the auth app, then back to `appPath` or the current page. */
  openAccount(appPath?: string): void {
    const returnUrl = appPath === undefined ? this.window.location.href : toAppUrl(appPath);
    this.window.location.assign(getAuthAppUrl('/account', { redirect_uri: returnUrl }));
  }

  /** Forwards a webapp path to the same page of the auth app, query params included (legacy email links). */
  forward(path: string, queryParams: Record<string, string>): void {
    this.window.location.assign(getAuthAppUrl(path, queryParams));
  }

  // The page is about to be left: abort the uploads still running. The store reset only clears memory.
  private leaveSession(): void {
    this.videoUploadService.abortActiveUploads();
    this.store.dispatch(lifeCycleActions.resetState());
  }
}

// Only a path is appended to `appUrl`: anything else (`@evil.com`, a full URL) could change the host.
const toAppUrl = (appPath: string): string => `${environment.appUrl}${appPath.startsWith('/') ? appPath : '/home'}`;

const getAuthAppUrl = (path: string, queryParams: Record<string, string>): string => {
  const url = new URL(path, environment.authAppUrl);
  Object.entries(queryParams).forEach(([name, value]) => url.searchParams.set(name, value));
  return url.toString();
};
